import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { apiGet, apiPost } from "@/services/api";

type VendorType = { id: string; name: string; slug: string };

export function SellPage() {
  const navigate = useNavigate();
  const [types, setTypes] = useState<VendorType[]>([]);
  const [existing, setExisting] = useState<{ status: string } | null>(null);
  const [form, setForm] = useState({
    businessName: "",
    vendorTypeId: "",
    ownerName: "",
    phone: "",
    address: "",
    description: "",
    businessLicense: "",
    taxId: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    void apiGet<{ vendorTypes: VendorType[] }>("/api/store/vendor-types")
      .then((d) => setTypes(d?.vendorTypes ?? []))
      .catch(() => setTypes([]));
    void apiGet<{ vendor: { status: string } }>("/api/vendors/me")
      .then((d) => setExisting(d?.vendor ?? null))
      .catch(() => setExisting(null));
  }, []);

  async function submit() {
    setSaving(true);
    try {
      await apiPost("/api/vendors/apply", form);
      toast.success("Application submitted — awaiting admin review");
      navigate("/");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not apply");
    } finally {
      setSaving(false);
    }
  }

  if (existing) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-10">
        <div className="amazon-card p-6">
          <h1 className="text-2xl font-black">Vendor application</h1>
          <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
            Your vendor account status is <strong>{existing.status}</strong>.
            {existing.status === "ACTIVE" && (
              <>
                {" "}
                Visit <a className="text-amazon-teal font-bold" href="/vendor">your seller dashboard</a>.
              </>
            )}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl px-3 py-6 sm:px-4 sm:py-10">
      <div className="amazon-card space-y-4 p-4 sm:p-6">
        <h1 className="text-2xl font-black">Become a vendor</h1>
        <p className="text-sm text-slate-600 dark:text-slate-300">
          Apply to sell on MOVA Market. An administrator reviews every application before it goes live.
        </p>
        {(
          [
            ["businessName", "Business name *"],
            ["ownerName", "Owner name"],
            ["phone", "Phone"],
            ["address", "Address"],
            ["description", "Description"],
            ["businessLicense", "Business license #"],
            ["taxId", "Tax ID"],
          ] as const
        ).map(([key, label]) => (
          <label key={key} className="block text-sm">
            <span className="font-bold">{label}</span>
            <input
              value={form[key]}
              onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
              className="mt-1 w-full rounded border border-slate-300 px-3 py-2 dark:border-white/10 dark:bg-white/5"
            />
          </label>
        ))}
        <label className="block text-sm">
          <span className="font-bold">Vendor type</span>
          <select
            value={form.vendorTypeId}
            onChange={(e) => setForm((f) => ({ ...f, vendorTypeId: e.target.value }))}
            className="mt-1 w-full rounded border border-slate-300 px-3 py-2 dark:border-white/10 dark:bg-white/5"
          >
            <option value="">Select a type…</option>
            {types.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </label>
        <Button disabled={saving || !form.businessName} onClick={() => void submit()}>
          {saving ? "Submitting…" : "Submit application"}
        </Button>
      </div>
    </div>
  );
}
